import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  CircularProgress,
  Alert,
  Breadcrumbs,
  Link,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Grid,
  Chip,
  FormControlLabel,
  Switch,
  FormGroup,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  SelectChangeEvent
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Person as PersonIcon,
  AdminPanelSettings as AdminIcon,
  ShoppingCart as BuyerIcon,
  Storefront as SellerIcon,
  Block as BlockIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  FilterList as FilterIcon
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import apiService from '../../services/api';
import { User, UserCreate, UserUpdate } from '../../types/models';
import { formatDate } from '../../utils/formatters';

const UsersList: React.FC = () => {
  const { user: currentUser } = useAuth();
  
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);
  
  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [verifiedFilter, setVerifiedFilter] = useState('');
  const [activeFilter, setActiveFilter] = useState('');
  const [createdAfter, setCreatedAfter] = useState('');
  const [createdBefore, setCreatedBefore] = useState('');
  const [orderBy, setOrderBy] = useState('created_at');
  const [orderDirection, setOrderDirection] = useState<'asc' | 'desc'>('desc');
  const [searchDebounce, setSearchDebounce] = useState<NodeJS.Timeout | null>(null);
  
  // User form dialog
  const [openFormDialog, setOpenFormDialog] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  
  // Form fields
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [governmentId, setGovernmentId] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isBuyer, setIsBuyer] = useState(false);
  const [isSeller, setIsSeller] = useState(false);
  
  // Delete confirmation dialog
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  
  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      // Build query parameters
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (roleFilter) params.append('role', roleFilter);
      if (verifiedFilter) params.append('email_verified', verifiedFilter);
      if (activeFilter) params.append('is_active', activeFilter);
      if (createdAfter) params.append('created_after', new Date(createdAfter).toISOString());
      if (createdBefore) params.append('created_before', new Date(createdBefore).toISOString());
      params.append('order_by', orderBy);
      params.append('order_direction', orderDirection);
      
      const queryString = params.toString();
      const url = queryString ? `/users?${queryString}` : '/users';
      
      const response = await apiService.get<User[]>(url);
      setUsers(response);
      setPage(0); // Reset to first page when filters change
    } catch (err: any) {
      console.error('Error fetching users:', err);
      setError(err.response?.data?.detail || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchUsers();
  }, []);
  
  // Debounced search effect
  useEffect(() => {
    if (searchDebounce) {
      clearTimeout(searchDebounce);
    }
    
    const timeout = setTimeout(() => {
      fetchUsers();
    }, 500); // 500ms debounce
    
    setSearchDebounce(timeout);
    
    return () => {
      if (searchDebounce) {
        clearTimeout(searchDebounce);
      }
    };
  }, [searchTerm]);
  
  // Fetch when filters change
  useEffect(() => {
    fetchUsers();
  }, [roleFilter, verifiedFilter, activeFilter, createdAfter, createdBefore, orderBy, orderDirection]);
  
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };
  
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  const handleClearFilters = () => {
    setSearchTerm('');
    setRoleFilter('');
    setVerifiedFilter('');
    setActiveFilter('');
    setCreatedAfter('');
    setCreatedBefore('');
    setOrderBy('created_at');
    setOrderDirection('desc');
  };
  
  const getActiveFilterCount = () => {
    let count = 0;
    if (searchTerm) count++;
    if (roleFilter) count++;
    if (verifiedFilter) count++;
    if (activeFilter) count++;
    if (createdAfter) count++;
    if (createdBefore) count++;
    return count;
  };
  
  const handleOpenCreateDialog = () => {
    setFormMode('create');
    setSelectedUser(null);
    resetForm();
    setOpenFormDialog(true);
  };
  
  const handleOpenEditDialog = (user: User) => {
    setFormMode('edit');
    setSelectedUser(user);
    
    // Populate form fields
    setEmail(user.email);
    setFullName(user.full_name);
    setPassword(''); // Don't populate password for security
    setPhoneNumber(user.phone_number || '');
    setGovernmentId(user.government_id || '');
    setIsActive(user.is_active);
    setIsAdmin(user.is_admin);
    setIsBuyer(user.is_buyer);
    setIsSeller(user.is_seller);
    
    setOpenFormDialog(true);
  };
  
  const handleCloseFormDialog = () => {
    setOpenFormDialog(false);
    setFormError(null);
  };
  
  const resetForm = () => {
    setEmail('');
    setFullName('');
    setPassword('');
    setPhoneNumber('');
    setGovernmentId('');
    setIsActive(true);
    setIsAdmin(false);
    setIsBuyer(false);
    setIsSeller(false);
  };
  
  const validateForm = () => {
    if (!email.trim()) {
      setFormError('Email is required');
      return false;
    }
    if (!fullName.trim()) {
      setFormError('Full name is required');
      return false;
    }
    if (formMode === 'create' && !password.trim()) {
      setFormError('Password is required for new users');
      return false;
    }
    
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setFormError('Please enter a valid email address');
      return false;
    }
    
    return true;
  };
  
  const handleSubmitForm = async () => {
    if (!validateForm()) return;
    
    setFormLoading(true);
    setFormError(null);
    
    try {
      if (formMode === 'create') {
        const userData: UserCreate = {
          email,
          full_name: fullName,
          password,
          phone_number: phoneNumber || undefined,
          government_id: governmentId || undefined,
          is_active: isActive,
          is_admin: isAdmin,
          is_buyer: isBuyer,
          is_seller: isSeller
        };
        
        await apiService.post('/users', userData);
        setActionSuccess('User created successfully');
      } else if (formMode === 'edit' && selectedUser) {
        const userData: UserUpdate = {
          email,
          full_name: fullName,
          phone_number: phoneNumber || undefined,
          government_id: governmentId || undefined,
          is_active: isActive,
          is_admin: isAdmin,
          is_buyer: isBuyer,
          is_seller: isSeller
        };
        
        // Only include password if it was changed
        if (password.trim()) {
          userData.password = password;
        }
        
        await apiService.put(`/users/${selectedUser.id}`, userData);
        setActionSuccess('User updated successfully');
      }
      
      // Refresh users list
      fetchUsers();
      
      // Close dialog
      handleCloseFormDialog();
    } catch (err: any) {
      console.error('Error saving user:', err);
      setFormError(err.response?.data?.detail || 'Failed to save user');
    } finally {
      setFormLoading(false);
    }
  };
  
  const handleOpenDeleteDialog = (user: User) => {
    setSelectedUser(user);
    setOpenDeleteDialog(true);
  };
  
  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
  };
  
  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    
    setDeleteLoading(true);
    
    try {
      await apiService.delete(`/users/${selectedUser.id}`);
      
      // Update local state
      setUsers(users.filter(user => user.id !== selectedUser.id));
      
      // Show success message
      setActionSuccess(`User "${selectedUser.full_name}" deleted successfully`);
      
      // Close dialog
      handleCloseDeleteDialog();
    } catch (err: any) {
      console.error('Error deleting user:', err);
      setError(err.response?.data?.detail || 'Failed to delete user');
      handleCloseDeleteDialog();
    } finally {
      setDeleteLoading(false);
    }
  };
  
  const getUserRoleChips = (user: User) => {
    return (
      <Box>
        {user.is_admin && (
          <Chip 
            icon={<AdminIcon />} 
            label="Admin" 
            color="primary" 
            size="small" 
            sx={{ mr: 0.5, mb: 0.5 }} 
          />
        )}
        {user.is_buyer && (
          <Chip 
            icon={<BuyerIcon />} 
            label="Buyer" 
            color="info" 
            size="small" 
            sx={{ mr: 0.5, mb: 0.5 }} 
          />
        )}
        {user.is_seller && (
          <Chip 
            icon={<SellerIcon />} 
            label="Seller" 
            color="success" 
            size="small" 
            sx={{ mr: 0.5, mb: 0.5 }} 
          />
        )}
        {!user.is_active && (
          <Chip 
            icon={<BlockIcon />} 
            label="Inactive" 
            color="error" 
            size="small" 
            sx={{ mb: 0.5 }} 
          />
        )}
      </Box>
    );
  };
  
  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link component={RouterLink} to="/dashboard" underline="hover" color="inherit">
          Panel de Control
        </Link>
        <Typography color="text.primary">Usuarios</Typography>
      </Breadcrumbs>
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" gutterBottom component="div">
          Users
        </Typography>
        <Box>
          <Button 
            variant="outlined" 
            startIcon={<RefreshIcon />}
            onClick={fetchUsers}
            sx={{ mr: 1 }}
          >
            Refresh
          </Button>
          <Button 
            variant="contained" 
            startIcon={<AddIcon />}
            onClick={handleOpenCreateDialog}
          >
            Add User
          </Button>
        </Box>
      </Box>
      
      {actionSuccess && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setActionSuccess(null)}>
          {actionSuccess}
        </Alert>
      )}
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      {/* Search and Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          {/* Search Bar */}
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="Buscar por nombre, email o cédula..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchTerm('')}>
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          
          {/* Role Filter */}
          <Grid item xs={12} sm={4} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Rol</InputLabel>
              <Select
                value={roleFilter}
                label="Rol"
                onChange={(e: SelectChangeEvent) => setRoleFilter(e.target.value)}
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
                <MenuItem value="buyer">Comprador</MenuItem>
                <MenuItem value="seller">Vendedor</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          {/* Verification Filter */}
          <Grid item xs={12} sm={4} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Verificado</InputLabel>
              <Select
                value={verifiedFilter}
                label="Verificado"
                onChange={(e: SelectChangeEvent) => setVerifiedFilter(e.target.value)}
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="true">Verificado</MenuItem>
                <MenuItem value="false">No verificado</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          {/* Active Status Filter */}
          <Grid item xs={12} sm={4} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Estado</InputLabel>
              <Select
                value={activeFilter}
                label="Estado"
                onChange={(e: SelectChangeEvent) => setActiveFilter(e.target.value)}
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="true">Activo</MenuItem>
                <MenuItem value="false">Inactivo</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          {/* Clear Filters Button */}
          <Grid item xs={12} sm={12} md={2}>
            <Button
              fullWidth
              variant="outlined"
              onClick={handleClearFilters}
              disabled={getActiveFilterCount() === 0}
              startIcon={<ClearIcon />}
            >
              Limpiar
            </Button>
          </Grid>
          
          {/* Date Filters - Second Row */}
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Creado después de"
              type="date"
              value={createdAfter}
              onChange={(e) => setCreatedAfter(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Creado antes de"
              type="date"
              value={createdBefore}
              onChange={(e) => setCreatedBefore(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          
          {/* Sort By */}
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Ordenar por</InputLabel>
              <Select
                value={orderBy}
                label="Ordenar por"
                onChange={(e: SelectChangeEvent) => setOrderBy(e.target.value)}
              >
                <MenuItem value="created_at">Fecha de creación</MenuItem>
                <MenuItem value="email">Email</MenuItem>
                <MenuItem value="full_name">Nombre</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          {/* Sort Direction */}
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Dirección</InputLabel>
              <Select
                value={orderDirection}
                label="Dirección"
                onChange={(e: SelectChangeEvent) => setOrderDirection(e.target.value as 'asc' | 'desc')}
              >
                <MenuItem value="desc">Más reciente primero</MenuItem>
                <MenuItem value="asc">Más antiguo primero</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
        
        {/* Active Filters Display */}
        {getActiveFilterCount() > 0 && (
          <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Filtros activos:
            </Typography>
            {searchTerm && (
              <Chip
                label={`Búsqueda: "${searchTerm}"`}
                size="small"
                onDelete={() => setSearchTerm('')}
              />
            )}
            {roleFilter && (
              <Chip
                label={`Rol: ${roleFilter === 'admin' ? 'Admin' : roleFilter === 'buyer' ? 'Comprador' : 'Vendedor'}`}
                size="small"
                onDelete={() => setRoleFilter('')}
              />
            )}
            {verifiedFilter && (
              <Chip
                label={`${verifiedFilter === 'true' ? 'Verificado' : 'No verificado'}`}
                size="small"
                onDelete={() => setVerifiedFilter('')}
              />
            )}
            {activeFilter && (
              <Chip
                label={`${activeFilter === 'true' ? 'Activo' : 'Inactivo'}`}
                size="small"
                onDelete={() => setActiveFilter('')}
              />
            )}
            {createdAfter && (
              <Chip
                label={`Desde: ${createdAfter}`}
                size="small"
                onDelete={() => setCreatedAfter('')}
              />
            )}
            {createdBefore && (
              <Chip
                label={`Hasta: ${createdBefore}`}
                size="small"
                onDelete={() => setCreatedBefore('')}
              />
            )}
            <Typography variant="body2" color="primary" sx={{ fontWeight: 'bold' }}>
              ({users.length} resultado{users.length !== 1 ? 's' : ''})
            </Typography>
          </Box>
        )}
      </Paper>
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : users.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h6">No users found</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Add a new user to get started
          </Typography>
          <Button 
            variant="contained" 
            startIcon={<AddIcon />}
            onClick={handleOpenCreateDialog}
          >
            Add User
          </Button>
        </Paper>
      ) : (
        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
          <TableContainer>
            <Table stickyHeader aria-label="users table">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>Government ID</TableCell>
                  <TableCell>Roles</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((user) => (
                    <TableRow 
                      key={user.id} 
                      hover
                      sx={{ 
                        opacity: user.is_active ? 1 : 0.6,
                        backgroundColor: user.id === currentUser?.id ? 'rgba(25, 118, 210, 0.08)' : 'inherit'
                      }}
                    >
                      <TableCell>{user.full_name}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>{user.phone_number || 'N/A'}</TableCell>
                      <TableCell>{user.government_id || 'N/A'}</TableCell>
                      <TableCell>{getUserRoleChips(user)}</TableCell>
                      <TableCell>{formatDate(user.created_at)}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex' }}>
                          <Tooltip title="Edit User">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenEditDialog(user)}
                              color="primary"
                              disabled={user.id === currentUser?.id} // Can't edit yourself
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete User">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenDeleteDialog(user)}
                              color="error"
                              disabled={user.id === currentUser?.id} // Can't delete yourself
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={users.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      )}
      
      {/* User Form Dialog */}
      <Dialog
        open={openFormDialog}
        onClose={handleCloseFormDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {formMode === 'create' ? 'Add New User' : 'Edit User'}
        </DialogTitle>
        <DialogContent>
          {formError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {formError}
            </Alert>
          )}
          
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Email"
                type="email"
                fullWidth
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={formLoading}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Full Name"
                fullWidth
                required
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                disabled={formLoading}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label={formMode === 'create' ? 'Password' : 'New Password (leave blank to keep current)'}
                type="password"
                fullWidth
                required={formMode === 'create'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={formLoading}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Phone Number"
                fullWidth
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                disabled={formLoading}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Government ID"
                fullWidth
                value={governmentId}
                onChange={(e) => setGovernmentId(e.target.value)}
                disabled={formLoading}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                User Roles and Status
              </Typography>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Switch 
                      checked={isActive} 
                      onChange={(e) => setIsActive(e.target.checked)}
                      disabled={formLoading}
                    />
                  }
                  label="Active"
                />
                <FormControlLabel
                  control={
                    <Switch 
                      checked={isAdmin} 
                      onChange={(e) => setIsAdmin(e.target.checked)}
                      disabled={formLoading}
                    />
                  }
                  label="Admin"
                />
                <FormControlLabel
                  control={
                    <Switch 
                      checked={isBuyer} 
                      onChange={(e) => setIsBuyer(e.target.checked)}
                      disabled={formLoading}
                    />
                  }
                  label="Buyer"
                />
                <FormControlLabel
                  control={
                    <Switch 
                      checked={isSeller} 
                      onChange={(e) => setIsSeller(e.target.checked)}
                      disabled={formLoading}
                    />
                  }
                  label="Seller"
                />
              </FormGroup>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseFormDialog} disabled={formLoading}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmitForm}
            variant="contained"
            disabled={formLoading}
            startIcon={formLoading ? <CircularProgress size={20} /> : null}
          >
            {formLoading 
              ? 'Saving...' 
              : formMode === 'create' 
                ? 'Create' 
                : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={handleCloseDeleteDialog}
      >
        {selectedUser && (
          <>
            <DialogTitle>
              Delete User
            </DialogTitle>
            <DialogContent>
              <DialogContentText>
                Are you sure you want to delete the user "{selectedUser.full_name}" ({selectedUser.email})? This action cannot be undone.
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDeleteDialog} disabled={deleteLoading}>
                Cancel
              </Button>
              <Button 
                onClick={handleDeleteUser}
                color="error"
                variant="contained"
                disabled={deleteLoading}
                startIcon={deleteLoading ? <CircularProgress size={20} /> : null}
              >
                {deleteLoading ? 'Deleting...' : 'Delete'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Container>
  );
};

export default UsersList;
